import React, { useState } from 'react';
import { 
  Box,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Paper,
  Tooltip,
  Typography,
  Chip,
  Avatar
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import { DataGrid, gridClasses } from '@mui/x-data-grid';

// Modern color palette that matches the KPI cards
const COLORS = {
  primary: '#2196f3',
  success: '#4caf50',
  warning: '#ffc107',
  purple: '#673ab7',
  background: '#f7f9fc',
  tableBorder: '#e3f2fd',
  hoverRow: alpha('#2196f3', 0.08)
};

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: 'white',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 1px 10px 0px rgba(0,0,0,0.025)',
  overflow: 'hidden',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: COLORS.primary,
    color: 'white',
    fontWeight: 'bold',
    borderBottom: 'none',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiIconButton-root': {
      color: 'white',
    },
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: alpha(COLORS.primary, 0.03),
    '&:hover': {
      backgroundColor: COLORS.hoverRow,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: COLORS.hoverRow,
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${alpha(COLORS.tableBorder, 0.5)}`,
    padding: '16px 8px',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${COLORS.tableBorder}`,
    backgroundColor: alpha(COLORS.primary, 0.05),
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: theme.spacing(2),
    backgroundColor: alpha(COLORS.primary, 0.02),
    borderBottom: `1px solid ${alpha(COLORS.tableBorder, 0.5)}`,
    gap: theme.spacing(2),
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(COLORS.primary, 0.3),
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
    backgroundColor: alpha(COLORS.primary, 0.05),
  },
}));

// Custom styled counter chip
const CounterChip = styled(Chip)(({ theme, count }) => ({
  fontWeight: 'bold',
  backgroundColor: count > 5 ? alpha(COLORS.success, 0.15) : alpha(COLORS.primary, 0.15),
  color: count > 5 ? COLORS.success : COLORS.primary,
  border: count > 5 ? `1px solid ${alpha(COLORS.success, 0.3)}` : `1px solid ${alpha(COLORS.primary, 0.3)}`,
  '& .MuiChip-avatar': {
    backgroundColor: count > 5 ? COLORS.success : COLORS.primary,
    color: 'white',
  }
}));

const FaqTable = ({ data }) => {
  const columns = [
    { 
      field: 'question_id', 
      headerName: 'Pregunta ID', 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => (
        <Typography sx={{ 
          fontWeight: 500,
          color: COLORS.primary,
          display: 'flex',
          alignItems: 'center'
        }}>
          <QuestionAnswerIcon 
            fontSize="small" 
            sx={{ 
              mr: 1, 
              opacity: 0.7,
              color: COLORS.primary 
            }} 
          />
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'question_text', 
      headerName: 'Texto de Pregunta', 
      flex: 2,
      minWidth: 300,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 400 }}>
          {params.value || '(Sin texto)'}
        </Typography>
      )
    },
    { 
      field: 'count', 
      headerName: 'Contador', 
      flex: 0.5, 
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <CounterChip
          count={params.value}
          avatar={<Avatar>{params.value}</Avatar>}
          label="veces"
          variant="outlined"
          size="small"
        />
      )
    }
  ];

  const rows = data.map((row, index) => ({
    id: row.question_id || index,
    ...row
  }));

  return (
    <Box sx={{ height: 'auto', width: '100%', position: 'relative' }}>
      <StripedDataGrid
        rows={rows}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[5, 10, 25]}
        autoHeight
        density="comfortable"
        disableColumnResize
        disableColumnFilter
        disableColumnMenu
        components={{
          NoRowsOverlay: () => (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              p: 3,
              color: 'text.secondary' 
            }}>
              <QuestionAnswerIcon sx={{ fontSize: 40, color: alpha(COLORS.primary, 0.3), mb: 1 }} />
              <Typography>No hay preguntas frecuentes disponibles</Typography>
            </Box>
          )
        }}
      />
    </Box>
  );
};

export default FaqTable; 